import type {
  AlertConfigAlertType,
  AlertConfigModule,
  AlertRuleConfig,
} from "../../../api/frameworksApi";

/** Simple option shape used by the scheduler form dropdowns. */
export interface SelectItem {
  value: string;
  label: string;
}

/* ── STOC operational alert types ────────────────────────────────────────── */
// Inlined from g360's `@features/stoc` constants — the Schedulers page only
// needs the alert-type labels, not the full STOC module.
export type StocAlertType =
  | "NotOnboarded"
  | "NotReporting"
  | "AgentFailure"
  | "ConfigDrift"
  | "PolicyExpired"
  | "VersionMismatch"
  | "MissingControl";

export const STOC_ALERT_TYPE_LABELS: Record<StocAlertType, string> = {
  NotOnboarded: "Asset Not Onboarded",
  NotReporting: "Asset Not Reporting",
  AgentFailure: "Agent Failure",
  ConfigDrift: "Configuration Drift",
  PolicyExpired: "Policy Expired",
  VersionMismatch: "Version Mismatch",
  MissingControl: "Missing Security Control",
};

// STOC operational alert types are addressed as `stoc:<Type>` so they share the
// scheduler's alert-type field with the legacy date/SLA types.
export type StocScheduleMode = `stoc:${StocAlertType}`;
export type EmailScheduleMode =
  | "reminder"
  | "expiry"
  | "unmapped_asset"
  | "sla_breach"
  | StocScheduleMode;

export const STOC_MODE_PREFIX = "stoc:";
export const isStocMode = (mode: string): mode is StocScheduleMode =>
  mode.startsWith(STOC_MODE_PREFIX);

const STOC_TYPE_OPTIONS: SelectItem[] = (
  Object.keys(STOC_ALERT_TYPE_LABELS) as StocAlertType[]
).map((type) => ({
  value: `${STOC_MODE_PREFIX}${type}`,
  label: STOC_ALERT_TYPE_LABELS[type],
}));
const STOC_MODES = STOC_TYPE_OPTIONS.map((o) => o.value as EmailScheduleMode);

export const ALERT_TYPE_OPTIONS: SelectItem[] = [
  { value: "reminder", label: "Target Date Reminder" },
  { value: "expiry", label: "Target Date Expiry" },
  { value: "unmapped_asset", label: "Unmapped Asset (On Prem)" },
  { value: "sla_breach", label: "SLA Breach" },
  ...STOC_TYPE_OPTIONS,
];

/* ── Module maps ─────────────────────────────────────────────────────────── */
export const MODULE_LABEL_MAP: Record<AlertConfigModule, string> = {
  VM: "Vulnerability Management",
  CM: "Configuration Management",
  CMDB: "CMDB",
  STOC: "Security Tools Operations Center",
};

export const MODULE_CODE_BY_VALUE: Record<string, AlertConfigModule> = {
  vulnerability_management: "VM",
  configuration_management: "CM",
  cmdb: "CMDB",
  stoc: "STOC",
};

export const VALUE_BY_MODULE_CODE = Object.fromEntries(
  Object.entries(MODULE_CODE_BY_VALUE).map(([value, code]) => [code, value]),
) as Record<AlertConfigModule, string>;

/* ── Alert-type ↔ mode ↔ api maps ────────────────────────────────────────── */
type LegacyMode = "reminder" | "expiry" | "unmapped_asset" | "sla_breach";

const LEGACY_MODE_LABEL: Record<LegacyMode, string> = {
  reminder: "Target Date Reminder",
  expiry: "Target Date Expiry",
  unmapped_asset: "Unmapped Asset (On Prem)",
  sla_breach: "SLA Breach",
};
const LEGACY_MODE_TO_API: Record<LegacyMode, AlertConfigAlertType> = {
  reminder: "TARGET_REMINDER",
  expiry: "TARGET_EXPIRY",
  unmapped_asset: "UNMAPPED_ASSET",
  sla_breach: "SLA_BREACH",
};
const LEGACY_API_TO_MODE: Partial<Record<AlertConfigAlertType, EmailScheduleMode>> = {
  TARGET_REMINDER: "reminder",
  TARGET_EXPIRY: "expiry",
  UNMAPPED_ASSET: "unmapped_asset",
  SLA_BREACH: "sla_breach",
};
const STOC_TYPE_TO_API: Record<StocAlertType, AlertConfigAlertType> = {
  NotOnboarded: "STOC_NOT_ONBOARDED",
  NotReporting: "STOC_NOT_REPORTING",
  AgentFailure: "STOC_AGENT_FAILURE",
  ConfigDrift: "STOC_CONFIG_DRIFT",
  PolicyExpired: "STOC_POLICY_EXPIRED",
  VersionMismatch: "STOC_VERSION_MISMATCH",
  MissingControl: "STOC_MISSING_CONTROL",
};
const STOC_API_TO_TYPE: Partial<Record<AlertConfigAlertType, StocAlertType>> = {
  STOC_NOT_ONBOARDED: "NotOnboarded",
  STOC_NOT_REPORTING: "NotReporting",
  STOC_AGENT_FAILURE: "AgentFailure",
  STOC_CONFIG_DRIFT: "ConfigDrift",
  STOC_POLICY_EXPIRED: "PolicyExpired",
  STOC_VERSION_MISMATCH: "VersionMismatch",
  STOC_MISSING_CONTROL: "MissingControl",
};

const stocTypeOfMode = (mode: EmailScheduleMode): StocAlertType =>
  mode.slice(STOC_MODE_PREFIX.length) as StocAlertType;

export const alertTypeLabelFromMode = (mode: EmailScheduleMode): string =>
  isStocMode(mode)
    ? STOC_ALERT_TYPE_LABELS[stocTypeOfMode(mode)] ?? "STOC Alert"
    : LEGACY_MODE_LABEL[mode];

export const alertTypeApiFromMode = (mode: EmailScheduleMode): AlertConfigAlertType =>
  isStocMode(mode) ? STOC_TYPE_TO_API[stocTypeOfMode(mode)] : LEGACY_MODE_TO_API[mode];

export const modeFromAlertTypeApi = (
  alertType?: AlertConfigAlertType,
): EmailScheduleMode => {
  if (!alertType) return "reminder";
  const stocType = STOC_API_TO_TYPE[alertType];
  if (stocType) return `${STOC_MODE_PREFIX}${stocType}` as EmailScheduleMode;
  return LEGACY_API_TO_MODE[alertType] ?? "reminder";
};

/* ── Which alert types each module exposes ───────────────────────────────── */
// Unmapped Asset is a CMDB-only concept; STOC modules expose the operational
// alert types; every other module exposes the date/SLA alert types.
export const DEFAULT_ALERT_TYPES: EmailScheduleMode[] = ["reminder", "expiry", "sla_breach"];
export const ALERT_TYPES_BY_MODULE: Record<string, EmailScheduleMode[]> = {
  cmdb: ["unmapped_asset"],
  stoc: STOC_MODES,
};

export const getAlertTypesFor = (module: string): SelectItem[] => {
  const allowed = ALERT_TYPES_BY_MODULE[module] ?? DEFAULT_ALERT_TYPES;
  return ALERT_TYPE_OPTIONS.filter((o) =>
    allowed.includes(o.value as EmailScheduleMode),
  );
};

// Target Date Expiry alerts repeat on an interval up to a maximum frequency.
export const usesIntervalAndFrequency = (mode: EmailScheduleMode) => mode === "expiry";
// Target Date Reminder and STOC alerts fire N units before the deadline.
export const usesAlertBefore = (mode: EmailScheduleMode) =>
  mode === "reminder" || isStocMode(mode);
// Unmapped Asset and SLA Breach use a Frequency/On/At cadence picker.
export const usesImportCadence = (mode: EmailScheduleMode) =>
  mode === "unmapped_asset" || mode === "sla_breach";

/* ── Row + modal payload shapes ──────────────────────────────────────────── */
export interface SchedulerRow {
  id: number;
  moduleName: string;
  moduleCode?: AlertConfigModule;
  alertType?: AlertConfigAlertType;
  time: string;
  importOn: string;
  importFrequency: string;
  status: boolean;
  tool: string;
  instanceName?: string;
  environment?: string;
  subject?: string;
  message?: string;
  alertConfig?: AlertRuleConfig;
  lastTriggeredDateStr: string;
  createdBy: string;
  createdDate: number;
  updatedBy: string;
  updatedDate: number;
}

export interface AddDataImportPayload {
  module: string;
  tool: string;
  instanceName?: string;
  environment?: string;
  importFrequency: string;
  importOn: string;
  importAt: string;
}

export interface AddEmailSchedulePayload {
  module: string;
  scheduleTime: string;
  mode: EmailScheduleMode;
  alertBefore: number | null;
  alertBeforeUnit: string;
  alertInterval: number | null;
  alertFrequency: number | null;
  importFrequency: string;
  importOn: string;
  importAt: string;
  subject: string;
  message: string;
}

export interface TemplateContent {
  subject: string;
  message: string;
}
export type TemplateMode = EmailScheduleMode;
export type TemplatesPayload = Partial<Record<TemplateMode, TemplateContent>>;

export const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const FREQUENCY_OPTIONS: SelectItem[] = [
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
];

export const STOC_TOOL_OPTIONS: SelectItem[] = [
  { value: "tenable_io", label: "Tenable IO" },
  { value: "tenable_security_center", label: "Tenable Security Center" },
];

